import { IMAGES } from "@/data/content";

const scrollToContact = (e) => {
  e.preventDefault();
  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
};

export function Hero() {
  return (
    <section id="hero" className="relative text-white overflow-hidden py-24 sm:py-32 lg:py-36 bg-[#0a1b2a]" data-testid="hero-section">
      <img
        src={IMAGES.hero}
        alt="Mother pouring fresh water for her daughter in a sunlit Oklahoma kitchen"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 hero-immersion" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl">
          <h1 className="editorial-title text-4xl sm:text-6xl lg:text-7xl font-normal leading-[1.08] text-white fade-in-up">
            What's really in your water?
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-200 leading-relaxed font-light max-w-2xl fade-in-up fade-in-up-delay-1">
            Your water may look clear in the glass. That does not mean it is working well for your home, your
            skin, or your family's appliances. Whitestone helps Oklahoma homeowners test first and understand
            every drop before making any decisions.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center fade-in-up fade-in-up-delay-2">
            <a href="#contact" onClick={scrollToContact} data-testid="hero-primary-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-xl text-base font-semibold text-slate-950 bg-sky-300 hover:bg-sky-200 shadow-lg shadow-sky-950/20 transition text-center">
              GET MY FREE WATER ANALYSIS
            </a>
            <a href="#test"
              onClick={(e) => { e.preventDefault(); document.querySelector("#test")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="hero-secondary-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-4 rounded-xl text-base font-medium text-slate-200 border border-white/20 hover:bg-white/10 transition text-center">
              See what we test in your home
            </a>
          </div>
          <p className="mt-4 text-xs tracking-wide text-slate-300 uppercase fade-in-up fade-in-up-delay-2">
            100% Educational In-Home Assessment &bull; Zero Sales Pressure &bull; Oklahoma City &amp; Tulsa Metro
          </p>
        </div>
      </div>
    </section>
  );
}

export function Problem() {
  return (
    <section id="curiosity" className="py-20 bg-white subtle-border border-b" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-inner">
              <img src={IMAGES.glass} alt="Sunlight through a glass of clean water at a kitchen counter"
                className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-x-4 bottom-4 water-glass-card p-4 rounded-xl border border-slate-200/80">
                <span className="text-xs font-bold uppercase tracking-wider text-sky-800">Observation</span>
                <p className="text-sm font-medium text-slate-800 mt-1">
                  Clear water can still carry dissolved minerals, municipal chlorine, and high hardness levels.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-800">The Problem</span>
            <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
              Your water looks clean. That doesn't tell the whole story.
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
              Most municipal water in Oklahoma meets baseline health safety standards when it leaves the
              treatment facility. But safety standards do not measure water hardness, residual disinfectant
              odor, or the gradual mineral scaling that wears on water heaters and dries out skin.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-slate-50 border border-slate-200/70">
                <h3 className="font-semibold text-slate-900 text-base">What You Notice Daily</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Spotty glassware, stiff laundry, tight itchy skin after showers, and lingering chlorine
                  smell in morning tap water.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-slate-50 border border-slate-200/70">
                <h3 className="font-semibold text-slate-900 text-base">What Stays Hidden</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Scale building inside pipes, stress on water heaters, and dissolved minerals quietly
                  affecting home fixtures.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const GALLERY = [
  { img: IMAGES.kitchen, tag: "Morning Coffee & Cooking", title: "Pure Taste in Every Recipe",
    text: "Crisp drinking water without chlorine aftertaste for teas, soups, baby formula, and pets." },
  { img: IMAGES.shower, tag: "Skin & Hair Care", title: "Softer Showers Every Day",
    text: "Conditioned water rinses soap cleanly without stripping natural moisture from skin and hair." },
  { img: IMAGES.laundry, tag: "Laundry & Linens", title: "Brighter, Longer-Lasting Fabrics",
    text: "Removes the mineral grit that frays towel fibers and keeps detergent from rinsing out clean." },
  { img: IMAGES.dishes, tag: "Dishes & Fixtures", title: "Sparkling Glass, No Scrubbing",
    text: "No cloudy haze on stemware or chalky calcium rings around black faucets and glass doors." },
];

export function Education() {
  return (
    <section className="py-20 bg-[#f4f9fd] border-b border-slate-200" data-testid="education-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Whole Home Perspective</span>
          <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
            Your water does more than you think
          </h2>
          <p className="mt-3 text-slate-600">
            Every glass. Every shower. Every load of laundry. Every dish. Every water-using appliance —
            from the first morning coffee to bedtime baths, water touches every routine under your roof.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {GALLERY.map((g, i) => (
            <div key={g.title} className="group bg-white rounded-2xl p-4 border border-slate-200 shadow-sm" data-testid={`gallery-card-${i}`}>
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-slate-100">
                <img src={g.img} alt={g.tag} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute left-3 bottom-3 text-xs font-semibold text-slate-700 bg-white/90 px-2.5 py-1 rounded-md">
                  {g.tag}
                </span>
              </div>
              <h3 className="font-bold text-slate-900 text-base mt-4">{g.title}</h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{g.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const REGIONS = [
  {
    tag: "OKC & Central Metro", style: "bg-[#0a1b2a]", badge: "bg-sky-900/60 text-sky-300 border-sky-700/50",
    title: "Hardness & Surface Lake Minerals",
    text: "Surface water from Lake Hefner, Draper, and Overholser shifts with the seasons, and hardness across the central metro leaves scale on fixtures and stress on water heaters.",
    footer: "Common in Edmond, Norman, Moore, Yukon", footerColor: "text-sky-200",
  },
  {
    tag: "Tulsa & Green Country", style: "bg-[#0f2b44]", badge: "bg-sky-900/60 text-sky-300 border-sky-700/50",
    title: "Chloramine & Disinfectant Taste",
    text: "Tulsa's municipal network uses chloramine for secondary disinfection. It keeps water safe in the pipes, but it takes specialized catalytic media — not a basic carbon filter — to remove the taste and odor at home.",
    footer: "Common in Broken Arrow, Bixby, Jenks, Owasso", footerColor: "text-sky-200",
  },
  {
    tag: "Suburban & Rural Private Wells", style: "bg-[#083344]", badge: "bg-teal-900/60 text-teal-300 border-teal-700/50",
    title: "Iron, Sediment & Variable pH",
    text: "Well water across Oklahoma can carry dissolved iron that stains porcelain orange, sulfur compounds behind rotten-egg odors, and pH levels that affect copper plumbing.",
    footer: "Tested across Logan, Canadian & Tulsa Counties", footerColor: "text-teal-200",
  },
];

export function LocalOK() {
  return (
    <section id="oklahoma" className="py-20 bg-white border-b border-slate-200" data-testid="oklahoma-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Regional Expertise</span>
          <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
            Oklahoma water has its own distinct challenges
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            Water chemistry in Oklahoma City differs from Tulsa, and well systems outside the metros face
            entirely different mineral loads. We test for what is actually in your local supply.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {REGIONS.map((r, i) => (
            <div key={r.tag} className={`p-6 rounded-2xl ${r.style} text-white flex flex-col justify-between`} data-testid={`region-card-${i}`}>
              <div>
                <span className={`inline-block px-3 py-1 ${r.badge} text-xs font-semibold rounded-full border`}>{r.tag}</span>
                <h3 className="text-xl font-bold mt-4">{r.title}</h3>
                <p className="text-slate-300 text-sm mt-2 leading-relaxed">{r.text}</p>
              </div>
              <div className={`mt-6 pt-4 border-t border-slate-800 text-xs ${r.footerColor} font-medium`}>{r.footer}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-base font-bold text-slate-900">Serving Homeowners Throughout Oklahoma</h3>
            <p className="text-sm text-slate-600 mt-1">
              Oklahoma City, Tulsa, Broken Arrow, Bixby, Jenks, Owasso, Edmond, Norman, Yukon, and Moore.
            </p>
          </div>
          <a href="/locations" data-testid="oklahoma-locations-cta"
            className="inline-flex shrink-0 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition">
            Check Your Neighborhood
          </a>
        </div>
      </div>
    </section>
  );
}
